import consumer from "./consumer"

console.log("TaskChannel: Starting to create subscription...");

let subscription = null;
let reconnectAttempts = 0;
const maxReconnectAttempts = 3;
const reconnectDelay = 5000; // 5 seconds
let isRejected = false;
let isConnecting = false;

// Fallback polling functionality
let pollingInterval = null;
let isPolling = false;
const pollingDelay = 30000; // 30 seconds
const maxPollingAttempts = 10;

function createSubscription() {
  console.log("TaskChannel: Creating new subscription...");

  if (!consumer) {
    console.error("TaskChannel: Consumer not available, cannot create subscription");
    return;
  }

  if (isRejected) {
    console.log("TaskChannel: Not creating subscription - previous connection was rejected");
    return;
  }

  if (isConnecting) {
    console.log("TaskChannel: Already connecting, skipping duplicate subscription attempt"); // Don't create multiple subscriptions
    return;
  }

  if (subscription) { // Clean up existing subscription if any
    console.log("TaskChannel: Cleaning up existing subscription");
    subscription.unsubscribe();
    subscription = null;
  }

  isConnecting = true;
  subscription = consumer.subscriptions.create("TaskChannel", {
    connected() {
      console.log("✅ Task WebSocket connected successfully!");
      console.log("TaskChannel: Connection established");
      reconnectAttempts = 0;
      isRejected = false;
      isConnecting = false;
      stopPolling();
    },

    disconnected() {
      console.log("❌ Task WebSocket disconnected");
      console.log("TaskChannel: Connection lost");
      isConnecting = false;

      if (reconnectAttempts < maxReconnectAttempts && !isRejected) {
        reconnectAttempts++;
        console.log(`TaskChannel: Attempting to reconnect (${reconnectAttempts}/${maxReconnectAttempts})...`);

        setTimeout(() => {
          createSubscription();
        }, reconnectDelay);
      } else {
        console.log("TaskChannel: Not attempting to reconnect - max attempts reached or connection was rejected");
        console.log("TaskChannel: Starting fallback polling due to WebSocket failure");
        startPolling();
      }
    },

    rejected() {
      console.log("❌ Task WebSocket connection rejected");
      console.log("TaskChannel: Connection was rejected - likely authentication issue");
      isRejected = true;
      isConnecting = false;
      console.log("TaskChannel: Starting fallback polling due to connection rejection");
      startPolling();
    },

    received(data) {
      console.log("🎯 TaskChannel: received() function called!");
      console.log("TaskChannel: Raw data received:", data);

      if (data && data.type === 'task_count_update') {
        console.log("TaskChannel: Processing task count update");
        console.log("TaskChannel: New count:", data.count);
        updateTaskBadgeFromWebSocket(data.count);
      } else {
        console.log("TaskChannel: Ignoring data - not a task_count_update");
      }
    }
  });

  console.log("TaskChannel: Subscription created:", subscription);
}

// Fallback polling functions
function startPolling() {
  if (isPolling) {
    console.log("TaskChannel: Polling already active");
    return;
  }

  console.log("TaskChannel: Starting fallback polling every", pollingDelay / 1000, "seconds");
  isPolling = true;

  pollTaskCount();

  pollingInterval = setInterval(() => {
    pollTaskCount();
  }, pollingDelay);
}

function stopPolling() {
  if (!isPolling) {
    return;
  }

  console.log("TaskChannel: Stopping fallback polling");
  isPolling = false;

  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
}

function pollTaskCount() {
  console.log("TaskChannel: Polling task count...");

  fetch('/task/new_tasks_count/task_count')
    .then(response => {
      if (response.status === 401 || response.status === 403) {
        console.log("TaskChannel: User not authenticated, stopping polling");
        stopPolling();
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (!data) return; // Handle case where we stopped due to auth issues

      if (data.status === 'OK') {
        console.log("TaskChannel: Polled task count:", data.task_count);
        updateTaskBadgeFromWebSocket(data.task_count);

        // Periodically try to reconnect to WebSocket (every 5th poll)
        if (!isRejected && Math.random() < 0.2) { // 20% chance per poll
          console.log("TaskChannel: Attempting to reconnect to WebSocket...");
          attemptWebSocketReconnect();
        }
      } else {
        console.error("TaskChannel: Polling failed - invalid response:", data);
      }
    })
    .catch(error => {
      console.error("TaskChannel: Polling error:", error);
    });
}

function attemptWebSocketReconnect() {
  if (subscription && subscription.connected) {
    console.log("TaskChannel: WebSocket already connected, skipping reconnect");
    return;
  }

  if (isConnecting) {
    console.log("TaskChannel: WebSocket connection already in progress");
    return;
  }

  console.log("TaskChannel: Attempting WebSocket reconnection from polling mode");
  reconnectAttempts = 0; // Reset attempts for new connection try
  createSubscription();
}

function updateTaskBadgeFromWebSocket(count) {
  console.log("TaskChannel: updateTaskBadge called with count:", count);

  const menuItem = document.getElementById('tasks-menu-item');
  if (!menuItem) {
    console.error("TaskChannel: Could not find #tasks-menu-item element");
    return;
  }

  let badge = menuItem.querySelector('.badge');

  if (badge) {
    console.log("TaskChannel: Updated existing badge");
    badge.textContent = count;
  } else {
    console.log("TaskChannel: Creating new badge");
    badge = document.createElement('span');
    badge.className = 'badge badge-danger';
    badge.textContent = count;
    menuItem.appendChild(badge);
  }

  if (count > 0) {
    badge.style.display = 'inline';
  } else {
    badge.style.display = 'none';
  }
}

// Check if user is logged in before starting WebSocket/polling
function checkUserAuthentication() {
  return fetch('/task/new_tasks_count/task_count')
    .then(response => {
      if (response.status === 401 || response.status === 403) {
        console.log("TaskChannel: User not authenticated, skipping WebSocket and polling");
        return false;
      }
      return response.json().then(data => {
        if (data.status === 'OK') {
          console.log("TaskChannel: User authenticated, proceeding with WebSocket");
          return true;
        }
        console.log("TaskChannel: Authentication check failed, skipping WebSocket and polling");
        return false;
      });
    })
    .catch(error => {
      console.log("TaskChannel: Authentication check error, skipping WebSocket and polling:", error);
      return false;
    });
}

// Only start WebSocket if user is authenticated
checkUserAuthentication().then(isAuthenticated => {
  if (isAuthenticated) {
    createSubscription();
  }
});

window.taskChannelSubscription = subscription;
